import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Bell,
  Settings,
  Send,
  History,
  TestTube,
  AlertTriangle,
  CheckCircle,
  Clock,
  Mail,
  MessageSquare,
  Smartphone,
} from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import Alert from "@/components/ui/alert";

interface NotificationConfig {
  reminderDaysBefore: number[];
  lowAttendanceThreshold: number;
  complianceThreshold: number;
  enableEmailNotifications: boolean;
  enableSMSNotifications: boolean;
  enablePushNotifications: boolean;
}

interface NotificationTemplate {
  id: string;
  type: 'meeting_reminder' | 'low_attendance' | 'compliance_warning' | 'meeting_summary';
  subject: string;
  body: string;
  variables: string[];
}

interface NotificationHistory {
  id: number;
  type: string;
  recipient: string;
  subject: string;
  sentAt: string;
  status: 'sent' | 'failed' | 'pending';
  metadata: any;
}

export default function NotificationManagement() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("config");
  const [isTestDialogOpen, setIsTestDialogOpen] = useState(false);
  const [testNotification, setTestNotification] = useState({
    type: 'meeting_reminder',
    recipient: '',
    templateData: {}
  });

  // Fetch notification configuration
  const { data: config, isLoading: configLoading } = useQuery<NotificationConfig>(
    "notification-config",
    async () => {
      const response = await api.get("/api/notifications/config");
      return response.data.data;
    }
  );

  // Fetch notification templates
  const { data: templates, isLoading: templatesLoading } = useQuery<NotificationTemplate[]>(
    "notification-templates",
    async () => {
      const response = await api.get("/api/notifications/templates");
      return response.data.data;
    }
  );

  // Fetch notification history
  const { data: history, isLoading: historyLoading } = useQuery<{
    notifications: NotificationHistory[];
    pagination: any;
  }>(
    "notification-history",
    async () => {
      const response = await api.get("/api/notifications/history");
      return response.data.data;
    }
  );

  // Update configuration mutation
  const updateConfigMutation = useMutation(
    async (newConfig: Partial<NotificationConfig>) => {
      const response = await api.put("/api/notifications/config", { config: newConfig });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success("Notification configuration updated successfully");
        queryClient.invalidateQueries("notification-config");
      },
      onError: (error: any) => {
        toast.error("Failed to update configuration", {
          description: error.response?.data?.message || "An unknown error occurred",
        });
      },
    }
  );

  // Process notifications mutation
  const processNotificationsMutation = useMutation(
    async () => {
      const response = await api.post("/api/notifications/process");
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success("Notifications processed successfully", {
          description: `${data.data.meetingReminders.length} reminders, ${data.data.lowAttendanceWarnings.length} warnings sent`,
        });
      },
      onError: (error: any) => {
        toast.error("Failed to process notifications", {
          description: error.response?.data?.message || "An unknown error occurred",
        });
      },
    }
  );

  // Test notification mutation
  const testNotificationMutation = useMutation(
    async (testData: any) => {
      const response = await api.post("/api/notifications/test", testData);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success("Test notification sent successfully");
        setIsTestDialogOpen(false);
      },
      onError: (error: any) => {
        toast.error("Failed to send test notification", {
          description: error.response?.data?.message || "An unknown error occurred",
        });
      },
    }
  );

  const handleConfigUpdate = (field: keyof NotificationConfig, value: any) => {
    if (config) {
      updateConfigMutation.mutate({
        ...config,
        [field]: value
      });
    }
  };

  const handleProcessNotifications = () => {
    processNotificationsMutation.mutate();
  };

  const handleSendTestNotification = () => {
    testNotificationMutation.mutate(testNotification);
  };

  const getNotificationTypeIcon = (type: string) => {
    switch (type) {
      case 'meeting_reminder': return <Bell className="w-4 h-4" />;
      case 'low_attendance': return <AlertTriangle className="w-4 h-4" />;
      case 'compliance_warning': return <AlertTriangle className="w-4 h-4" />;
      case 'meeting_summary': return <CheckCircle className="w-4 h-4" />;
      default: return <Bell className="w-4 h-4" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'sent': return <Badge variant="default" className="bg-green-100 text-green-800">Sent</Badge>;
      case 'failed': return <Badge variant="destructive">Failed</Badge>;
      case 'pending': return <Badge variant="secondary">Pending</Badge>;
      default: return <Badge variant="outline">Unknown</Badge>;
    }
  };

  if (configLoading || templatesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#fffffb' }}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#050500' }}>
              Notification Management
            </h1>
            <p className="text-lg" style={{ color: '#050500', opacity: 0.7 }}>
              Configure and manage automated notifications
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              onClick={handleProcessNotifications}
              disabled={processNotificationsMutation.isLoading}
              style={{ backgroundColor: '#e1f812', color: '#050500' }}
            >
              <Send className="w-4 h-4 mr-2" />
              {processNotificationsMutation.isLoading ? "Processing..." : "Process Notifications"}
            </Button>
          </div>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="config">Configuration</TabsTrigger>
            <TabsTrigger value="templates">Templates</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
            <TabsTrigger value="test">Test</TabsTrigger>
          </TabsList>

          {/* Configuration Tab */}
          <TabsContent value="config" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Notification Settings
                </CardTitle>
                <CardDescription>
                  Configure when and how notifications are sent
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Reminder Settings */}
                <div className="space-y-4">
                  <h4 className="font-semibold">Meeting Reminders</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Remind before meetings (days)</Label>
                      <div className="flex gap-2 mt-2">
                        {[1, 3, 7].map((day) => (
                          <Button
                            key={day}
                            variant={config?.reminderDaysBefore.includes(day) ? "default" : "outline"}
                            size="sm"
                            onClick={() => {
                              if (config) {
                                const newDays = config.reminderDaysBefore.includes(day)
                                  ? config.reminderDaysBefore.filter(d => d !== day)
                                  : [...config.reminderDaysBefore, day];
                                handleConfigUpdate('reminderDaysBefore', newDays.sort());
                              }
                            }}
                          >
                            {day} day{day > 1 ? 's' : ''}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Threshold Settings */}
                <div className="space-y-4">
                  <h4 className="font-semibold">Attendance Thresholds</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="lowAttendanceThreshold">Low Attendance Threshold (%)</Label>
                      <Input
                        id="lowAttendanceThreshold"
                        type="number"
                        value={config?.lowAttendanceThreshold || 70}
                        onChange={(e) => handleConfigUpdate('lowAttendanceThreshold', parseInt(e.target.value))}
                        min="0"
                        max="100"
                      />
                    </div>
                    <div>
                      <Label htmlFor="complianceThreshold">Compliance Threshold (%)</Label>
                      <Input
                        id="complianceThreshold"
                        type="number"
                        value={config?.complianceThreshold || 80}
                        onChange={(e) => handleConfigUpdate('complianceThreshold', parseInt(e.target.value))}
                        min="0"
                        max="100"
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Notification Channels */}
                <div className="space-y-4">
                  <h4 className="font-semibold">Notification Channels</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        <span>Email Notifications</span>
                      </div>
                      <Switch
                        checked={config?.enableEmailNotifications || false}
                        onCheckedChange={(checked) => handleConfigUpdate('enableEmailNotifications', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        <span>SMS Notifications</span>
                      </div>
                      <Switch
                        checked={config?.enableSMSNotifications || false}
                        onCheckedChange={(checked) => handleConfigUpdate('enableSMSNotifications', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Smartphone className="w-4 h-4" />
                        <span>Push Notifications</span>
                      </div>
                      <Switch
                        checked={config?.enablePushNotifications || false}
                        onCheckedChange={(checked) => handleConfigUpdate('enablePushNotifications', checked)}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Notification Templates</CardTitle>
                <CardDescription>
                  Manage email and SMS templates for different notification types
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {templates?.map((template) => (
                    <div key={template.id} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {getNotificationTypeIcon(template.type)}
                          <h4 className="font-semibold capitalize">
                            {template.type.replace('_', ' ')}
                          </h4>
                        </div>
                        <Badge variant="outline">{template.type}</Badge>
                      </div>
                      
                      <div className="space-y-2">
                        <div>
                          <Label className="text-sm font-medium">Subject</Label>
                          <div className="p-2 bg-gray-50 rounded text-sm">
                            {template.subject}
                          </div>
                        </div>
                        <div>
                          <Label className="text-sm font-medium">Body</Label>
                          <div className="p-2 bg-gray-50 rounded text-sm max-h-32 overflow-y-auto">
                            {template.body}
                          </div>
                        </div>
                        <div>
                          <Label className="text-sm font-medium">Variables</Label>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {template.variables.map((variable) => (
                              <Badge key={variable} variant="secondary" className="text-xs">
                                {`{${variable}}`}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Notification History
                </CardTitle>
                <CardDescription>
                  View sent notifications and their status
                </CardDescription>
              </CardHeader>
              <CardContent>
                {historyLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                  </div>
                ) : (
                  <ScrollArea className="h-96">
                    <div className="space-y-3">
                      {history?.notifications.map((notification) => (
                        <div key={notification.id} className="p-3 border rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {getNotificationTypeIcon(notification.type)}
                              <span className="font-medium">{notification.subject}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {getStatusBadge(notification.status)}
                              <span className="text-sm text-muted-foreground">
                                {format(new Date(notification.sentAt), "MMM d, yyyy HH:mm")}
                              </span>
                            </div>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            To: {notification.recipient}
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Test Tab */}
          <TabsContent value="test" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TestTube className="w-5 h-5" />
                  Test Notifications
                </CardTitle>
                <CardDescription>
                  Send test notifications to verify your configuration
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="testType">Notification Type</Label>
                    <Select
                      value={testNotification.type}
                      onValueChange={(value) => setTestNotification({ ...testNotification, type: value as any })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="meeting_reminder">Meeting Reminder</SelectItem>
                        <SelectItem value="low_attendance">Low Attendance Warning</SelectItem>
                        <SelectItem value="compliance_warning">Compliance Warning</SelectItem>
                        <SelectItem value="meeting_summary">Meeting Summary</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="testRecipient">Recipient Email</Label>
                    <Input
                      id="testRecipient"
                      type="email"
                      value={testNotification.recipient}
                      onChange={(e) => setTestNotification({ ...testNotification, recipient: e.target.value })}
                      placeholder="Enter email address for test notification"
                    />
                  </div>
                  
                  <Button
                    onClick={handleSendTestNotification}
                    disabled={testNotificationMutation.isLoading || !testNotification.recipient}
                    className="w-full"
                    style={{ backgroundColor: '#e1f812', color: '#050500' }}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {testNotificationMutation.isLoading ? "Sending..." : "Send Test Notification"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
